import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Markdown from "markdown-to-jsx"
import removeMd from "remove-markdown"
import { format } from "date-fns"
import { pl } from "date-fns/locale"
import { useTranslation } from "react-i18next"
import { getDateFnsLocale } from "@/lib/i18n/format"
import { cn } from "../../../utils/utils"
import { TableBlock, TableCellValue, TableColumnType } from "./types"
import {
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table"
import { Button, Icon } from "@blueprintjs/core"
import GalleryLink from "../../chat/SlickAIGalleryLink"
import { useAppDispatch, useAppSelector } from "../../../reducers/hooks"
import { changeActiveChat } from "../../../reducers/chatsSlice"
import { driversProfiles } from "../../../reducers/profilesSliceSelectors"
import { selectChats } from "../../../reducers/chatsSliceSelectors"

export const TableRenderer = ({
  block,
  messageId,
}: {
  block: TableBlock
  messageId: string
}) => {
  const { t } = useTranslation()
  const [sorting, setSorting] = useState<SortingState>([])
  const [copySuccess, setCopySuccess] = useState(false)

  const dispatch = useAppDispatch()
  const profiles = useAppSelector(driversProfiles)
  const chats = useAppSelector(selectChats)

  const handleDriverClick = useCallback(
    (driverName: string): void => {
      const driverProfile = profiles.find(
        (profile) => profile.name === driverName && profile.role === "driver",
      )

      if (!driverProfile) return

      const driverChat = chats.find((chat) =>
        chat.participants.some(
          (participant) => participant.jid === driverProfile.jid,
        ),
      )

      if (!driverChat) return

      dispatch(changeActiveChat({ activeChatJid: driverChat.jid }))
    },
    [profiles, chats, dispatch],
  )

  const formatCellForDisplay = (
    cell: TableCellValue,
    columnType: TableColumnType,
  ) => {
    if (cell === null) return ""
    if (columnType === "image" && typeof cell === "object" && "url" in cell) {
      return (
        <div className="max-h-[150px] max-w-[200px] overflow-hidden">
          <GalleryLink href={cell.url} messageId={messageId}>
            <img
              src={cell.url}
              alt={t("ai.table.imageAlt")}
              className="h-auto max-h-[40px] min-h-[40px] w-full object-contain"
            />
          </GalleryLink>
        </div>
      )
    }

    if (columnType === "date" && typeof cell === "string") {
      try {
        const date = new Date(cell)
        return format(date, "dd.MM.yyyy", { locale: getDateFnsLocale(pl) })
      } catch {
        return <Markdown>{String(cell)}</Markdown>
      }
    }

    if (columnType === "datetime" && typeof cell === "string") {
      try {
        const date = new Date(cell)
        return format(date, "dd.MM.yyyy HH:mm", {
          locale: getDateFnsLocale(pl),
        })
      } catch {
        return <Markdown>{String(cell)}</Markdown>
      }
    }

    if (columnType === "number" && typeof cell === "number") {
      try {
        return <span className="tabular-nums">{Number(cell).toFixed(0)}</span>
      } catch {
        return <Markdown>{String(cell)}</Markdown>
      }
    }

    if (columnType === "text" && typeof cell === "string") {
      const driverProfile = profiles.find(
        (profile) => profile.name === cell && profile.role === "driver",
      )

      if (driverProfile) {
        return (
          <button
            onClick={() => handleDriverClick(cell)}
            className="text-blue-500 hover:text-blue-700 hover:underline"
          >
            {cell}
          </button>
        )
      }
    }

    return <Markdown>{String(cell)}</Markdown>
  }

  // Transform array-based data into an object structure for TanStack
  const data = useMemo(() => {
    return block.rows.map((row) => {
      const rowData: Record<string, TableCellValue> = {}
      block.columns.forEach((column, colIndex) => {
        rowData[column.header] = row[colIndex]
      })
      return rowData
    })
  }, [block.rows, block.columns])

  const columns = useMemo(() => {
    return block.columns.map((column) => {
      return {
        accessorKey: column.header,
        header: column.header,
      }
    })
  }, [block.columns])

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  })

  const copyToExcel = () => {
    // Extract column headers
    const headers = block.columns
      .map((column) => removeMd(column.header))
      .join("\t")

    // Get the currently sorted rows from the table
    const sortedRows = table.getRowModel().rows

    // Generate rows with the current sort order
    const rows = sortedRows
      .map((row) => {
        // For each row, get all the cells in the correct order
        return block.columns
          .map((column, colIndex) => {
            // Get the value from the current row using the column header as the key
            const cell = row.original[column.header]
            const columnType = column.type
            let cellText = ""

            if (
              columnType === "image" &&
              typeof cell === "object" &&
              "url" in cell
            ) {
              cellText = cell.url
            } else if (
              (columnType === "date" || columnType === "datetime") &&
              typeof cell === "string"
            ) {
              try {
                const date = new Date(cell)
                cellText =
                  columnType === "date"
                    ? format(date, "dd.MM.yyyy", {
                        locale: getDateFnsLocale(pl),
                      })
                    : format(date, "dd.MM.yyyy HH:mm:ss", {
                        locale: getDateFnsLocale(pl),
                      })
              } catch {
                cellText = String(cell)
              }
            } else if (cell !== null) {
              cellText = String(cell)
            } else {
              cellText = ""
            }

            // Strip any markdown formatting from the cell text
            return removeMd(cellText)
          })
          .join("\t")
      })
      .join("\n")

    const tableData = `${headers}\n${rows}`

    // Copy to clipboard
    navigator.clipboard
      .writeText(tableData)
      .then(() => {
        setCopySuccess(true)
        window.appToaster.show({
          message: t("ai.table.copied"),
          intent: "success",
        })
      })
      .catch((err) => console.error("Failed to copy to clipboard:", err))
  }

  const tableRef = useRef<HTMLTableElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [buttonContainerWidth, setButtonContainerWidth] = useState("100%")

  // Update button container width when table width changes
  useEffect(() => {
    if (!tableRef.current) return

    const updateButtonWidth = () => {
      if (tableRef.current) {
        setButtonContainerWidth(`${tableRef.current.offsetWidth}px`)
      }
    }

    // Initial measurement
    updateButtonWidth()

    // Set up resize observer to track table width changes
    const resizeObserver = new ResizeObserver(updateButtonWidth)
    resizeObserver.observe(tableRef.current)

    return () => {
      if (tableRef.current) {
        resizeObserver.unobserve(tableRef.current)
      }
    }
  }, [])

  return (
    <div className="-mx-2.5 my-4 overflow-x-auto bg-white py-2.5 pb-5 dark:bg-[#1A1A1A]">
      <div ref={containerRef} className="w-full">
        {/* Button container with width matched to table */}
        <div
          className="flex w-full justify-end"
          style={{ maxWidth: buttonContainerWidth }}
        >
          <Button
            className="flex items-center rounded-md bg-blue-100 px-3 py-1 text-xs text-blue-800 transition-colors hover:bg-blue-200 dark:bg-blue-800 dark:text-blue-100 dark:hover:bg-blue-700"
            icon={
              copySuccess ? (
                <Icon icon="tick" size={12} />
              ) : (
                <Icon icon="duplicate" size={12} />
              )
            }
            minimal
            small
            onClick={copyToExcel}
            tabIndex={-1}
            intent={copySuccess ? "success" : "none"}
          >
            {t("common.copy")}
          </Button>
        </div>
      </div>
      <table
        ref={tableRef}
        className="min-w-fit divide-y divide-gray-200 border border-gray-200 dark:border-gray-700"
      >
        <thead className="bg-gray-50 dark:bg-gray-800">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header, idx) => (
                <th
                  key={header.id}
                  className={cn(
                    "cursor-pointer select-none px-4 py-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300",
                    block.columns[idx].type === "number"
                      ? "text-right"
                      : "text-left",
                  )}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <div className="flex items-center justify-between">
                    <Markdown>
                      {header.column.columnDef.header as string}
                    </Markdown>
                    <span className="ml-1">
                      {(() => {
                        const sort = header.column.getIsSorted()
                        if (sort === false) {
                          return (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              className="opacity-20"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                              />
                            </svg>
                          )
                        } else if (sort === "asc") {
                          return (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 15l7-7 7 7"
                              />
                            </svg>
                          )
                        } else {
                          return (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          )
                        }
                      })()}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
          {table.getRowModel().rows.map((row, rowIndex) => (
            <tr
              key={row.id}
              className={
                rowIndex % 2 === 0
                  ? "bg-white dark:bg-gray-900"
                  : "bg-gray-50 dark:bg-gray-800"
              }
            >
              {row.getVisibleCells().map((cell, cellIndex) => (
                <td
                  key={cell.id}
                  className={cn(
                    "px-4 py-2 text-sm text-gray-900 dark:text-gray-100",
                    block.columns[cellIndex].type === "number"
                      ? "text-right tabular-nums"
                      : "",
                  )}
                >
                  {formatCellForDisplay(
                    cell.getValue() as unknown as TableCellValue,
                    block.columns[cellIndex].type,
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
